/**
 * <s-story-slider> rotates the featured stories at the top of the blog page.
 * Slides are [data-slide] children; buttons with [data-go-to="<index>"] (the side
 * thumbnails and the mobile bars) jump to a slide. It auto-advances every
 * data-interval ms and pauses while hovered, focused or off-screen. Shoppers who
 * prefer reduced motion get no auto-advance. Mobile also supports swipe.
 *
 * Timeline: [data-progress] (the desktop line) fills from the top as the stories play,
 * one segment per story. [data-bar-fill] inside each mobile bar fills for the current
 * story; earlier bars stay full. Pausing freezes the timeline where it is.
 */
class StorySlider extends HTMLElement {
  #ac;
  #index = 0;
  #timer = 0;
  #paused = false;
  #visible = true;
  #elapsed = 0;
  #startedAt = 0;
  #running = false;

  get #interval() {
    return Number(this.dataset.interval) || 3000;
  }

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    if (this.#slides().length < 2) return;

    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-go-to]') : null;
      if (!button || !this.contains(button)) return;
      this.#show(Number(button.getAttribute('data-go-to')) || 0);
      this.#play();
    }, { signal });

    const pause = () => { this.#paused = true; this.#play(); };
    const resume = () => { this.#paused = false; this.#play(); };
    this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') pause(); }, { signal });
    this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') resume(); }, { signal });
    this.addEventListener('focusin', pause, { signal });
    this.addEventListener('focusout', (e) => {
      if (!(e.relatedTarget instanceof Node) || !this.contains(e.relatedTarget)) resume();
    }, { signal });

    let startX = 0;
    let startY = 0;
    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true, signal });
    this.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.#show(this.#index + (dx < 0 ? 1 : -1));
      this.#play();
    }, { passive: true, signal });

    const observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#play();
    });
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());
    document.addEventListener('visibilitychange', () => this.#play(), { signal });

    this.#show(0);
    this.#play();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-slide]')]);
  }

  /** @param {number} next */
  #show(next) {
    const slides = this.#slides();
    const count = slides.length;
    if (!count) return;
    this.#index = ((next % count) + count) % count;
    this.#elapsed = 0;
    this.#running = false;
    slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.toggleAttribute('data-active', active);
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    for (const button of this.querySelectorAll('[data-go-to]')) {
      const active = Number(button.getAttribute('data-go-to')) === this.#index;
      button.toggleAttribute('data-active', active);
      if (active) button.setAttribute('aria-current', 'true');
      else button.removeAttribute('aria-current');
    }
  }

  #canPlay() {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    return !reduce && !this.#paused && this.#visible && !document.hidden;
  }

  /** Starts, resumes or freezes the current story's countdown and timeline. */
  #play() {
    clearTimeout(this.#timer);
    if (this.#running) {
      this.#elapsed += performance.now() - this.#startedAt;
      this.#running = false;
    }
    const interval = this.#interval;
    const done = Math.min(this.#elapsed / interval, 1);

    if (!this.#canPlay()) {
      // Reduced motion shows the timeline at the current story without movement.
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#setTimeline(reduce ? 1 : done, 0);
      return;
    }

    const remaining = Math.max(interval - this.#elapsed, 0);
    this.#setTimeline(done, 0);
    // Force the start position to apply before animating to the end of this story.
    void this.offsetHeight;
    this.#setTimeline(1, remaining);
    this.#startedAt = performance.now();
    this.#running = true;
    this.#timer = window.setTimeout(() => {
      this.#show(this.#index + 1);
      this.#play();
    }, remaining);
  }

  /**
   * @param {number} fraction - How far through the current story (0 to 1).
   * @param {number} duration - Animation time in ms; 0 jumps straight there.
   */
  #setTimeline(fraction, duration) {
    const count = this.#slides().length;
    const transition = duration > 0 ? `${duration}ms linear` : 'none';

    const line = /** @type {HTMLElement | null} */ (this.querySelector('[data-progress]'));
    if (line) {
      line.style.transition = duration > 0 ? `height ${transition}` : 'none';
      line.style.height = `${((this.#index + fraction) / count) * 100}%`;
    }

    this.querySelectorAll('[data-go-to]').forEach((button) => {
      const fill = /** @type {HTMLElement | null} */ (button.querySelector('[data-bar-fill]'));
      if (!fill) return;
      const i = Number(button.getAttribute('data-go-to'));
      const isCurrent = i === this.#index;
      fill.style.transition = isCurrent && duration > 0 ? `width ${transition}` : 'none';
      fill.style.width = i < this.#index ? '100%' : isCurrent ? `${fraction * 100}%` : '0%';
    });
  }
}

if (!customElements.get('s-story-slider')) {
  customElements.define('s-story-slider', StorySlider);
}
